import axios from 'axios'
import { getBaseURL, getErrorMessage, getToken, removeToken } from './utils'
import { Toast } from 'vant'
import { i18n } from '@/locales'

/**
 * @description 创建axios实例
 */
function createService() {
  const service = axios.create({
    baseURL: getBaseURL(),
    timeout: 20000
  })

  /** 跳转登录页（与 VueRouter hash 模式一致） */
  const goLogin = (delayMs = 1500) => {
    setTimeout(() => {
      window.location.hash = '#/login'
    }, delayMs)
  }

  /**
   * 401 时是否自动跳转登录页：仅当请求发出时本地没有 token（未登录态访问需登录接口）时才跳转。
   * 已登录用户因 token 失效等收到 401 时只清 token、提示错误，留在当前页，避免打断定价/支付等流程。
   */
  const shouldRedirectToLoginOn401 = (config) => {
    const sentAuth = !!(config && config.headers && config.headers.Authorization)
    return !sentAuth
  }

  // 请求拦截器
  service.interceptors.request.use(
    config => {
      const token = getToken()
      if (token) {
        config.headers.Authorization = 'JWT ' + token
      }

      const method = config.method?.toUpperCase() || 'GET'
      const url = config.url || ''
      const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url

      console.log(`%c[请求] ${method} ${fullUrl}`, 'color: #1890ff; font-weight: bold;')
      if (config.params) {
        console.log('请求参数(Query):', config.params)
      }
      if (config.data) {
        console.log('请求参数(Body):', config.data)
      }

      return config
    },
    error => {
      console.error('请求错误:', error)
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  service.interceptors.response.use(
    response => {
      const dataAxios = response.data || null
      const method = response.config?.method?.toUpperCase() || 'GET'
      const url = response.config?.url || ''
      const fullUrl = response.config?.baseURL ? `${response.config.baseURL}${url}` : url

      console.log(`%c[响应] ${method} ${fullUrl}`, 'color: #52c41a; font-weight: bold;')
      console.log('响应数据:', dataAxios)

      // 文件下载
      if (response.headers['content-disposition']) {
        return dataAxios
      }

      const { code } = dataAxios

      // 第三方接口无code字段
      if (code === undefined) {
        return dataAxios
      }

      switch (code) {
        case 2000:
          return dataAxios
        case 401:
          Toast.fail(i18n.t('api.error.authExpired'))
          removeToken()
          if (shouldRedirectToLoginOn401(response.config)) {
            goLogin(1500)
          }
          return Promise.reject(new Error(i18n.t('api.error.authInvalid')))
        case 404:
          Toast.fail(i18n.t('api.error.resourceNotFound'))
          return Promise.reject(new Error(dataAxios.msg))
        case 4000:
          Toast.fail(getErrorMessage(dataAxios.msg))
          return Promise.reject(new Error(dataAxios.msg))
        case 400:
          Toast.fail(dataAxios.msg)
          return Promise.reject(new Error(dataAxios.msg))
        default:
          Toast.fail(`${dataAxios.msg}: ${response.config.url}`)
          return Promise.reject(new Error(dataAxios.msg))
      }
    },
    error => {
      if (error.response) {
        const { status } = error.response
        let message = i18n.t('api.error.requestError')

        switch (status) {
          case 400:
            message = i18n.t('api.error.requestError')
            break
          case 401:
            message = i18n.t('api.error.authExpired')
            removeToken()
            if (shouldRedirectToLoginOn401(error.config)) {
              goLogin(1500)
            }
            break
          case 403:
            message = i18n.t('api.error.forbidden')
            break
          case 404:
            message = i18n.t('api.error.urlNotFound')
            break
          case 408:
            message = i18n.t('api.error.timeout')
            break
          case 500:
            message = i18n.t('api.error.serverError')
            break
          case 502:
            message = i18n.t('api.error.badGateway')
            break
          case 503:
            message = i18n.t('api.error.serviceUnavailable')
            break
          case 504:
            message = i18n.t('api.error.gatewayTimeout')
            break
          default:
            break
        }

        console.error('请求错误:', message)
        Toast.fail(message)
      } else if (error.message) {
        console.error('请求失败:', error.message)
        Toast.fail(error.message)
      } else {
        Toast.fail(i18n.t('api.error.requestFailed'))
      }

      return Promise.reject(error)
    }
  )

  return service
}

export const service = createService()

/**
 * @description 通用请求方法
 */
export function request(config) {
  return service(config)
}
